import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { MyContext } from "./MyContext";
import { dateISOtoStringShortUTC } from "./momentUtils";
import { TableHeader2 } from "./formComponents";
import COLORS from "./styleConstants";
import Auth from "./Auth";

const formatTimestamp = (value, compact = false) => {
  if (!value) return "-";
  const timestamp = moment.utc(
    value,
    [moment.ISO_8601, "YYYY-MM-DD HH:mm:ss.SSSSSS", "YYYY-MM-DD HH:mm:ss"],
    true,
  );
  if (!timestamp.isValid()) return "-";
  return compact
    ? timestamp.local().locale("en").format("MMM D, YYYY [at] h:mm A")
    : timestamp.toDate().toLocaleString();
};
const formatDate = (value) => (value ? dateISOtoStringShortUTC(value) : "-");

const formatAmount = (amount, currency) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency?.toUpperCase() || "USD",
    }).format(numericAmount);
  } catch {
    return `${numericAmount.toFixed(2)} ${currency || ""}`.trim();
  }
};

export const AdminSubscriptionPaymentFailures = () => {
  const { dispatch } = useContext(MyContext);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchFailures = async () => {
      setLoading(true);
      setError("");
      dispatch({ type: "FETCH_INIT" });

      try {
        const result = await Auth.api(
          "subscriptions/getSubscriptionPaymentFailures",
          "get",
        );
        if (cancelled) return;

        setCharges(result?.data?.subscription_charges || []);
        dispatch({ type: "FETCH_SUCCESS" });
      } catch (requestError) {
        if (cancelled) return;
        setError(
          requestError.response?.data?.message ||
            requestError.message ||
            "Unable to load subscription payment failures.",
        );
        dispatch({ type: "FETCH_ERROR", error: requestError });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFailures();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  return (
    <div
      style={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        padding: 20,
      }}
    >
      {loading ? (
        <div>Loading payment failures...</div>
      ) : error ? (
        <div role="alert" style={{ color: "#b42318", marginBottom: 12 }}>
          {error}
        </div>
      ) : charges.length === 0 ? (
        <div>No subscription payment failures found.</div>
      ) : (
        <>
          <TableHeader2 title="Failed Subscription Charges" />
          <div style={{ width: "100%", overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: 420,
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ color: COLORS.blue, textAlign: "left" }}>
                  <th style={{ padding: "8px 6px" }}>Account</th>
                  <th style={{ padding: "8px 6px", textAlign: "right" }}>
                    Amount
                  </th>
                  <th style={{ padding: "8px 6px" }}>Last Charge Attempt</th>
                </tr>
              </thead>
              <tbody>
                {charges.map((charge) => (
                  <tr
                    key={charge.id}
                    style={{ borderTop: "1px solid #e2e5e9" }}
                  >
                    <td
                      style={{ padding: "8px 6px", overflowWrap: "anywhere" }}
                    >
                      {charge.billing_person_name || "-"}
                    </td>
                    <td style={{ padding: "8px 6px", textAlign: "right" }}>
                      {formatAmount(charge.total, charge.currency)}
                    </td>
                    <td style={{ padding: "8px 6px" }}>
                      {formatTimestamp(charge.status_updated_at, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {false && (
            <div
              style={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflowX: "auto",
                boxSizing: "border-box",
              }}
            >
              <table
                style={{
                  width: 1740,
                  borderCollapse: "collapse",
                  tableLayout: "fixed",
                  fontSize: 13,
                }}
              >
                <colgroup>
                  <col style={{ width: 129 }} />
                  <col style={{ width: 112 }} />
                  <col style={{ width: 60 }} />
                  <col style={{ width: 138 }} />
                  <col style={{ width: 138 }} />
                  <col style={{ width: 86 }} />
                  <col style={{ width: 103 }} />
                  <col style={{ width: 138 }} />
                  <col style={{ width: 65 }} />
                  <col style={{ width: 134 }} />
                  <col style={{ width: 138 }} />
                  <col style={{ width: 112 }} />
                  <col style={{ width: 198 }} />
                  <col style={{ width: 189 }} />
                </colgroup>
                <thead>
                  <tr style={{ color: COLORS.blue, textAlign: "left" }}>
                    <th>Account</th>
                    <th>Subscription status</th>
                    <th>Active</th>
                    <th>Scheduled charge</th>
                    <th>Package period</th>
                    <th>Total</th>
                    <th>Charge status</th>
                    <th>Status updated</th>
                    <th>Attempts</th>
                    <th>Latest attempt</th>
                    <th>Latest attempt status</th>
                    <th>Failure code</th>
                    <th>Failure message</th>
                    <th>PaymentIntent</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((charge) => (
                    <tr
                      key={charge.id}
                      style={{
                        borderTop: "1px solid #e2e5e9",
                        verticalAlign: "top",
                      }}
                    >
                      <td style={{ padding: "8px 6px" }}>
                        {charge.billing_person_name ||
                          charge.billing_person_id ||
                          "-"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {charge.subscription_status || "-"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {charge.subscription_is_active == null
                          ? "-"
                          : charge.subscription_is_active
                            ? "Yes"
                            : "No"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {formatTimestamp(charge.scheduled_charge_at)}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {formatDate(charge.package_start_date)}
                        {" – "}
                        {formatDate(charge.package_end_date)}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "right" }}>
                        {formatAmount(charge.total, charge.currency)}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {charge.status || "-"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {formatTimestamp(charge.status_updated_at)}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {charge.attempt_count ?? 0}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {charge.latest_payment_attempt_id ?? "-"}
                        {charge.latest_attempt_at && (
                          <div>{formatTimestamp(charge.latest_attempt_at)}</div>
                        )}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {charge.latest_attempt_status || "-"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {charge.failure_code || "-"}
                      </td>
                      <td
                        style={{
                          padding: "8px 6px",
                          maxWidth: 260,
                          overflowWrap: "anywhere",
                          whiteSpace: "normal",
                        }}
                      >
                        {charge.failure_message || "-"}
                      </td>
                      <td
                        style={{
                          padding: "8px 6px",
                          maxWidth: 220,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {charge.stripe_payment_intent_id || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
